/**
 * @fileoverview Environment configuration module
 * @module @puffin-core/subdomain-routing/config/environment
 *
 * @description
 * Provides testable environment checks and configuration access.
 * Abstracts environment detection to work in both Vite (import.meta) and Jest (process.env).
 *
 * This module uses a dual-mode approach:
 * - In Vite/browser: Uses import.meta.env
 * - In Jest/Node: Uses process.env
 *
 * This enables:
 * 1. Code to work in both environments
 * 2. Easy mocking in tests
 * 3. Single source of truth for environment checks
 */

/**
 * Check if we're in a browser/Vite environment
 * @private
 * @returns {boolean} True if in browser environment
 */
function isBrowserEnvironment() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Get the current environment mode
 * @private
 * @returns {string} Current mode (development, production, test, preview)
 * @throws {Error} when neither Vite's `MODE` nor `NODE_ENV` names a tier
 */
function getMode() {
  // In browser: Vite injects import.meta.env.MODE (development/production/preview) at build time — it is always
  // present, so the `||` short-circuits and `process.env.NODE_ENV` (replaced statically by Vite anyway) is never read.
  // In Jest: babel rewrites import.meta.env → process.env, so MODE is usually undefined and NODE_ENV (Jest sets 'test')
  // answers. With NEITHER set there is no tier to read, and it is refused by name: an unset tier is never defaulted to
  // 'development', the relaxed tier (UNSET-TIER-FAILS-CLOSED-001, owner ruling Q-EE-42 "Fail closed").
  const mode = import.meta.env.MODE || process.env.NODE_ENV;
  if (!mode) {
    throw new Error(
      "@puffin-core/subdomain-routing: no tier to read — neither Vite's MODE nor NODE_ENV is set",
    );
  }
  return mode;
}

/**
 * The ONLY env keys browser code reads through this module, each a NAMED `import.meta.env` read (SECURITY-HARDENING-GEMS § A).
 *
 * Vite replaces a named read with that one value. A computed `import.meta.env[key]` (what this module used to do) makes it inline
 * the WHOLE env object, i.e. every `VITE_*` key the app's spec sets, into the public bundle of every app that bundles
 * SubdomainRouter. That is how parcelpuffin.com published its `VITE_ENCRYPTION_KEY`. Built per call, so a test that sets the
 * environment between reads is honoured (babel rewrites these reads to `process.env` under Jest).
 *
 * @returns {Object<string, string|undefined>}
 */
function browserEnv() {
  return {
    VITE_API_URL: import.meta.env.VITE_API_URL,
    VITE_BASE_DOMAIN: import.meta.env.VITE_BASE_DOMAIN,
    VITE_DEV_SUBDOMAINS: import.meta.env.VITE_DEV_SUBDOMAINS,
  };
}

/** The keys `browserEnv` declares. */
export const BROWSER_ENV_KEYS = Object.freeze(Object.keys(browserEnv()));

/**
 * Get environment variable value
 * @private
 * @param {string} key - Environment variable key
 * @returns {string|undefined} Environment variable value
 * @throws {Error} In the browser, for a key `browserEnv` does not declare: it would read `undefined` in production, silently.
 */
function getEnvVar(key) {
  // In Jest/Node, use process.env directly. The presence test is written where `process` is read, in the ONE guard
  // shape a reader and the browser-reach judge both see (DUAL-USE-ESM-REACH-001): this module is bundled into every
  // web app, where `process` does not exist.
  if (!isBrowserEnvironment()) {
    if (typeof process === 'undefined') {
      throw new Error(
        '@puffin-core/subdomain-routing: neither a browser (window + document) nor Node (process) — no environment to read',
      );
    }
    return process.env[key];
  }
  if (!BROWSER_ENV_KEYS.includes(key)) {
    throw new Error(
      `"${key}" is not a declared browser env key of @puffin-core/subdomain-routing. Add a NAMED import.meta.env read for it ` +
        'to browserEnv() in config/environment.js (a computed read would inline every VITE_* key into the bundle).',
    );
  }
  return browserEnv()[key];
}

// Cache the mode for performance
let cachedMode = null;

/**
 * Get the cached or fresh mode
 * @private
 * @returns {string} Current mode
 */
function getCachedMode() {
  if (cachedMode === null) {
    cachedMode = getMode();
  }
  return cachedMode;
}

/**
 * Reset the cached mode (useful for testing)
 */
export function resetModeCache() {
  cachedMode = null;
}

/**
 * Check if running in development mode
 * @returns {boolean} True if in development mode
 */
export function isDevelopment() {
  return getCachedMode() === 'development';
}

/**
 * Check if running in production mode
 * @returns {boolean} True if in production mode
 */
export function isProduction() {
  return getCachedMode() === 'production';
}

/**
 * Check if running in test mode
 * @returns {boolean} True if in test mode
 */
export function isTest() {
  const mode = getCachedMode();
  if (mode === 'test') {
    return true;
  }
  // Node-only fallback: Jest may set NODE_ENV='test' while MODE differs. In the
  // browser there is no `process`, so isBrowserEnvironment() gates the read
  // (replacing the prior untestable `typeof process` guard) — its false arm
  // (Node) is covered by the node-environment test file, its true arm (browser)
  // by the jsdom suite.
  return !isBrowserEnvironment() && process.env.NODE_ENV === 'test';
}

/**
 * The DEPLOYMENT's app mode — THE web-side reader of the deployment tier (SEC-B deployment-tier
 * unit, 2026-09-30, owner ruling "keep soft launch (fix it)").
 *
 * It is `VITE_APP_MODE` (`development` | `preview` | `production`), which each deployment declares
 * (ParcelPuffin: `preview`, the soft launch). It is NOT Vite's `MODE`: every deployed web app is
 * built with plain `vite build`, so `MODE` is always `production` there, and a preview check on
 * `MODE` never fired in production — the signup wizard was open while the deployment declared the
 * soft launch. Read by NAME so Vite inlines this one key; with no declaration the build mode
 * stands in (local development, and builds that declare nothing).
 *
 * @returns {string} The app mode
 */
export function getAppMode() {
  return import.meta.env.VITE_APP_MODE || getCachedMode();
}

/**
 * Is this deployment the preview soft launch (`VITE_APP_MODE=preview`)? The marketing site is
 * public, demo signup captures leads, and the signup wizard is OFF.
 *
 * @returns {boolean} True in the preview soft launch
 */
export function isPreview() {
  return getAppMode() === 'preview';
}

/**
 * Check if the signup wizard should be accessible
 * In preview mode, the signup wizard is disabled - users are captured as leads only
 * @returns {boolean} True if signup wizard should be accessible
 */
export function isSignupWizardEnabled() {
  return !isPreview();
}

/**
 * Get the current mode
 * @returns {string} Current mode (development, production, test)
 */
export function getEnvironmentMode() {
  return getCachedMode();
}

/**
 * Get environment variable by key
 * @param {string} key - Environment variable key
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Environment variable value or default
 */
export function getEnvironmentVariable(key, defaultValue = undefined) {
  const value = getEnvVar(key);
  return value !== undefined ? value : defaultValue;
}

/**
 * Environment configuration object
 * Provides a centralized interface to all environment settings
 */
export const environment = {
  mode: getCachedMode(),
  isDevelopment: isDevelopment(),
  isProduction: isProduction(),
  isTest: isTest(),
  isPreview: isPreview(),
  isSignupWizardEnabled: isSignupWizardEnabled(),

  get currentMode() {
    return getCachedMode();
  },

  getVariable: getEnvironmentVariable,
};

export default environment;
